import React, { useState, useRef, useEffect } from 'react';

/**
 * Confirmation dialog for issuing a refund.
 *
 * Shared by the Master Admin subscription refund and the tenant admin loan
 * payment refund. Both need the same guarantees, so they get the same
 * component rather than two that drift apart:
 *
 *   - nothing is sent until the admin has seen exactly what will be refunded
 *   - a double-click cannot submit twice (ref lock plus disabled button)
 *   - the resulting balance is shown BEFORE anything is sent
 *   - it is usable on a phone
 *
 * The submit lock is a ref, not state. State updates are asynchronous, so two
 * clicks landing in the same render pass would both read the old value and both
 * fire. A ref is written synchronously and the second click sees it.
 *
 * @param {object}   props
 * @param {boolean}  props.isOpen
 * @param {function} props.onClose
 * @param {function} props.onSubmit    async ({amount, reason, extra}) => void
 * @param {string}   props.title
 * @param {number}   props.maxRefundable
 * @param {number}   props.alreadyRefunded
 * @param {string}   props.methodLabel  How the money moves back.
 * @param {Array}    props.lines        [{label, value, muted}] payment summary.
 * @param {function} [props.renderEffect]  (amount) => node, shown as the result.
 * @param {node}     [props.warning]    Shown above the buttons, e.g. escrow note.
 * @param {node}     [props.extraFields]
 */
function RefundModal({
  isOpen,
  onClose,
  onSubmit,
  title = 'Issue Refund',
  maxRefundable = 0,
  alreadyRefunded = 0,
  methodLabel = '',
  lines = [],
  renderEffect,
  warning,
  extraFields
}) {
  const [mode, setMode] = useState('full');
  const [partialAmount, setPartialAmount] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const submitLockRef = useRef(false);

  // Reset every time the dialog opens, so a previous refund's amount and
  // reason can never be carried into the next one.
  useEffect(() => {
    if (isOpen) {
      setMode('full');
      setPartialAmount('');
      setReason('');
      setError('');
      setSubmitting(false);
      submitLockRef.current = false;
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const refundAmount = mode === 'full'
    ? maxRefundable
    : parseFloat(partialAmount || '0');

  const amountValid =
    Number.isFinite(refundAmount) &&
    refundAmount > 0 &&
    refundAmount <= maxRefundable + 0.004;

  const canSubmit = amountValid && reason.trim().length > 0 && !submitting;

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Synchronous gate: the second of two fast clicks returns here.
    if (submitLockRef.current) return;
    if (!canSubmit) return;

    submitLockRef.current = true;
    setSubmitting(true);
    setError('');

    try {
      await onSubmit({ amount: Number(refundAmount.toFixed(2)), reason: reason.trim() });
    } catch (err) {
      setError(err?.message || 'Refund failed');
      // Only released on failure. On success the parent closes the dialog, and
      // leaving the lock set until then stops a retry racing the close.
      submitLockRef.current = false;
      setSubmitting(false);
    }
  };

  const money = (value) =>
    `$${(Number(value) || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;

  return (
    <div
      onClick={submitting ? undefined : onClose}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        zIndex: 1000,
        overflowY: 'auto'
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: 'var(--color-surface)',
          borderRadius: '8px',
          padding: '20px',
          width: '100%',
          maxWidth: '480px',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxSizing: 'border-box'
        }}
      >
        <h3 style={{ marginTop: 0, marginBottom: '4px', fontSize: '1.15rem' }}>{title}</h3>
        {methodLabel && (
          <div style={{ fontSize: '0.85rem', color: 'var(--color-text-light)', marginBottom: '14px' }}>
            {methodLabel}
          </div>
        )}

        {/* What is being refunded */}
        <div style={{
          backgroundColor: 'var(--color-surface-sunken)',
          borderRadius: '6px',
          padding: '12px',
          marginBottom: '14px',
          fontSize: '0.88rem'
        }}>
          {lines.map((line, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: '12px',
                padding: '3px 0',
                color: line.muted ? '#666' : 'inherit',
                fontWeight: line.strong ? 600 : 400
              }}
            >
              <span>{line.label}</span>
              <span style={{ textAlign: 'right' }}>{line.value}</span>
            </div>
          ))}
          {alreadyRefunded > 0 && (
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '12px',
              padding: '3px 0',
              color: 'var(--color-error)'
            }}>
              <span>Already refunded</span>
              <span>-{money(alreadyRefunded)}</span>
            </div>
          )}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: '12px',
            padding: '6px 0 0',
            marginTop: '6px',
            borderTop: '1px solid var(--color-border)',
            fontWeight: 600
          }}>
            <span>Still refundable</span>
            <span>{money(maxRefundable)}</span>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '6px' }}>
              Refund amount
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.9rem' }}>
                <input
                  type="radio"
                  name="refundMode"
                  checked={mode === 'full'}
                  onChange={() => setMode('full')}
                  disabled={submitting}
                />
                Full ({money(maxRefundable)})
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.9rem' }}>
                <input
                  type="radio"
                  name="refundMode"
                  checked={mode === 'partial'}
                  onChange={() => setMode('partial')}
                  disabled={submitting}
                />
                Partial
              </label>
            </div>

            {mode === 'partial' && (
              <input
                type="number"
                step="0.01"
                min="0.01"
                max={maxRefundable}
                inputMode="decimal"
                value={partialAmount}
                onChange={(e) => setPartialAmount(e.target.value)}
                placeholder={`Up to ${money(maxRefundable)}`}
                disabled={submitting}
                autoFocus
                style={{
                  width: '100%',
                  padding: '10px',
                  marginTop: '8px',
                  fontSize: '16px', // 16px stops iOS Safari zooming on focus
                  minHeight: '44px',
                  boxSizing: 'border-box'
                }}
              />
            )}
          </div>

          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '6px' }}>
              Reason <span style={{ color: 'var(--color-error)' }}>*</span>
            </label>
            <textarea
              rows="2"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this being refunded?"
              disabled={submitting}
              style={{
                width: '100%',
                padding: '10px',
                fontSize: '16px',
                resize: 'vertical',
                boxSizing: 'border-box'
              }}
            />
          </div>

          {extraFields}

          {/* The resulting state, computed from the amount actually entered. */}
          {amountValid && renderEffect && (
            <div style={{
              backgroundColor: 'var(--color-info-bg)',
              border: '1px solid var(--color-info-border)',
              borderRadius: '6px',
              padding: '12px',
              marginBottom: '12px',
              fontSize: '0.88rem'
            }}>
              {renderEffect(refundAmount)}
            </div>
          )}

          {warning && (
            <div style={{
              backgroundColor: 'var(--color-warning-bg)',
              border: '1px solid var(--color-warning-border)',
              borderRadius: '6px',
              padding: '10px 12px',
              marginBottom: '12px',
              fontSize: '0.82rem',
              color: 'var(--color-warning-text)'
            }}>
              {warning}
            </div>
          )}

          {error && (
            <div style={{
              backgroundColor: 'var(--color-error-bg)',
              border: '1px solid var(--color-error-border)',
              borderRadius: '6px',
              padding: '10px 12px',
              marginBottom: '12px',
              fontSize: '0.85rem',
              color: 'var(--color-error-text)'
            }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
            <button
              type="submit"
              disabled={!canSubmit}
              style={{
                flex: '1 1 160px',
                padding: '12px',
                fontSize: '0.95rem',
                fontWeight: 600,
                color: '#fff',
                backgroundColor: canSubmit ? '#dc3545' : '#e9a0a8',
                border: 'none',
                borderRadius: '4px',
                cursor: canSubmit ? 'pointer' : 'not-allowed'
              }}
            >
              {submitting ? 'Processing…' : `Refund ${money(refundAmount > 0 ? refundAmount : 0)}`}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              style={{
                flex: '1 1 120px',
                padding: '12px',
                fontSize: '0.95rem',
                backgroundColor: '#6c757d',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: submitting ? 'not-allowed' : 'pointer'
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default RefundModal;
